/* ══ 채팅 겉창이 읽는 문 — 전부 이미 있는 API 다 ══
   이 이슈(t-in-chat-drawer)는 **API 를 만들지 않는다.** 여기 적힌 모양은
   `crates/api/src/{sessions,messages,events,tasks,dispatch}.rs` 가 내는 그대로다.
   화면이 판정하지 않는다 — 말/진행은 서버가 접은 `role`·`kind`, 단계는 세션 기록의
   `track` 과 큐의 `track`, 전달 상태는 우편함 행의 사실 셋으로 그린다. */
import { tx } from "../i18n";

export const API = import.meta.env.VITE_B3XIN_API_URL ?? "/api";

/** `GET /agent-sessions` 한 줄(`sessions.rs` `session_json`). */
export type Session = {
  id: string;
  taskId: string;
  taskTitle: string | null;
  runner: string;
  track: string | null;
  vendor: string | null;
  outcome: string | null;
  /** 세션이 밝힌 모델. **null 은 「모름」**이다(안 밝혔거나 옛 기록). */
  model?: string | null;
  note: string | null;
  startedAt: string | null;
  endedAt: string | null;
  /** 사람이 멈추라 한 시각 — 끝나기 전까지 「멈추는 중」의 근거. */
  stopRequestedAt: string | null;
  /** **null 은 「못 읽었다」다 — 0 이 아니다.** */
  inputTokens: number | null;
  outputTokens: number | null;
  lineCount: number;
};

export type LogRole = "message" | "progress" | "raw";

/** `GET /agent-sessions/:id/log` 의 한 줄. `role`·`kind` 는 벤더가 접은 값이다(`Vendor::fold`). */
export type LogEntry = {
  seq: number;
  at: string;
  role: LogRole;
  kind: string;
  text: string;
  label: string | null;
  summary: string | null;
  body: string | null;
  isError: boolean;
};

export type LogWindow = {
  entries: LogEntry[];
  total: number;
  first: number | null;
  last: number | null;
  hasPrev: boolean;
  hasMore: boolean;
};

/** `GET /tasks/:id/messages` 의 한 줄 — 전달은 **사실 셋**(집어 감 · 그때의 세션 · 그 세션이
 *  끝남)이고 「전달 못 함」 문장은 화면이 쓴다(`deliveryText`). */
export type Message = {
  id: string;
  taskId: string;
  body: string;
  createdBy: string;
  createdAt: string;
  sessionId: string | null;
  deliveredAt: string | null;
  deliveredTo: string | null;
  sessionEndedAt: string | null;
};

/** `GET /tasks/:id/events` 의 한 줄 — 상태 이동(`kind: status`)과 발자국을 한 줄기로. */
export type TaskEvent = {
  taskId: string;
  taskTitle: string;
  kind: string;
  actor: string;
  summary: string;
  from: string | null;
  to: string | null;
  at: string;
};

/** 이슈 고르기가 읽는 만큼만(`GET /tasks`). */
export type TaskRow = {
  id: string;
  title: string;
  status: string;
  updatedAt: string | null;
  pendingMessages?: number;
};

/** `GET /dispatch/queue` 한 줄 — 가리켜진 이슈의 **다음 트랙**은 서버가 정한 값이다. */
export type QueueRow = {
  task: { id: string };
  track: string | null;
  skip: string | null;
};

/** 단계 띠의 다섯 칸 — `crates/track` 의 `Track::ALL` 과 같은 순서. 모르는 트랙이 오면
 *  띠가 그 이름 그대로 한 칸을 더 그린다(`StageStrip`) — 값을 버리지 않는다. */
export const TRACKS = [
  { key: "plan", label: tx("chat.track.plan") },
  { key: "design", label: tx("chat.track.design") },
  { key: "run", label: tx("chat.track.run") },
  { key: "review", label: tx("chat.track.review") },
  { key: "merge", label: tx("chat.track.merge") },
] as const;

/** 트랙이 없는 세션을 단계 띠 · 탭이 한 칸으로 모으는 이름(키이자 글자). */
export const NO_TRACK = tx("chat.noTrack");

export function trackLabel(track: string | null): string {
  if (!track) return tx("chat.trackUnknown");
  return TRACKS.find((t) => t.key === track)?.label ?? track;
}

class HttpError extends Error {
  constructor(
    public status: number,
    public path: string,
    msg: string,
  ) {
    super(msg);
  }
}

/** 실패는 셋을 말한다 — 무엇이(HTTP) · 어디서(주소) · 서버의 문장. */
async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${API}${path}`, init);
  if (r.ok) return (await r.json()) as T;
  let msg = `HTTP ${r.status}`;
  try {
    const j = (await r.json()) as { message?: string; error?: string };
    if (j.message) msg = `${j.message} (HTTP ${r.status})`;
    else if (j.error) msg = `${j.error} (HTTP ${r.status})`;
  } catch {
    // 본문이 JSON 이 아니면 상태 코드만 말한다.
  }
  throw new HttpError(r.status, `${API}${path}`, msg);
}

const post = (path: string, body?: unknown) =>
  call<unknown>(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });

export const chatApi = {
  sessions: (task?: string, limit = 200) =>
    call<Session[]>(`/agent-sessions?limit=${limit}${task ? `&task=${encodeURIComponent(task)}` : ""}`),
  session: (id: string) => call<Session>(`/agent-sessions/${encodeURIComponent(id)}`),
  logTail: (id: string, limit = 200) => call<LogWindow>(`/agent-sessions/${encodeURIComponent(id)}/log?limit=${limit}`),
  logBefore: (id: string, before: number, limit = 200) =>
    call<LogWindow>(`/agent-sessions/${encodeURIComponent(id)}/log?before=${before}&limit=${limit}`),
  logAfter: (id: string, after: number, limit = 500) =>
    call<LogWindow>(`/agent-sessions/${encodeURIComponent(id)}/log?after=${after}&limit=${limit}`),
  stop: (id: string) => post(`/agent-sessions/${encodeURIComponent(id)}/stop`) as Promise<Session>,
  tasks: () => call<TaskRow[]>(`/tasks`),
  events: (task: string, limit = 200) => call<TaskEvent[]>(`/tasks/${encodeURIComponent(task)}/events?limit=${limit}`),
  messages: (task: string) => call<Message[]>(`/tasks/${encodeURIComponent(task)}/messages`),
  leave: (task: string, body: string) =>
    post(`/tasks/${encodeURIComponent(task)}/messages`, { body }) as Promise<Message>,
  queue: () => call<QueueRow[]>(`/dispatch/queue`),
};

export function errText(e: unknown): string {
  if (e instanceof HttpError) return `${e.message} — ${e.path}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

/* ── 시간 말 ──
   **지난 일과 도는 중은 다른 말이다.** 끝난 것은 「3분 전」, 도는 것은 「12분째」. */
export function agoText(iso: string | null, now = Date.now()): string {
  if (!iso) return tx("chat.time.unknown");
  const min = Math.round((now - Date.parse(iso)) / 60_000);
  if (min < 1) return tx("issue.since.now");
  if (min < 60) return tx("chat.time.minAgo", { n: min });
  const h = Math.floor(min / 60);
  return h < 24 ? tx("chat.time.hourAgo", { n: h }) : tx("chat.time.dayAgo", { n: Math.floor(h / 24) });
}

export function runningText(startIso: string | null, now = Date.now()): string {
  if (!startIso) return tx("chat.time.startUnknown");
  const min = Math.round((now - Date.parse(startIso)) / 60_000);
  if (min < 1) return tx("chat.time.justStarted");
  if (min < 60) return tx("issue.since.min", { n: min });
  const h = Math.floor(min / 60);
  return h < 24 ? tx("issue.since.hour", { n: h }) : tx("issue.since.day", { n: Math.floor(h / 24) });
}

/** 끝난 세션이 걸린 시간. 시작이나 끝을 모르면 「모름」 — 0 이 아니다. */
export function durationText(startIso: string | null, endIso: string | null): string {
  if (!startIso || !endIso) return tx("chat.time.durUnknown");
  const sec = Math.max(0, Math.round((Date.parse(endIso) - Date.parse(startIso)) / 1000));
  if (sec < 60) return tx("chat.time.sec", { n: sec });
  const min = Math.round(sec / 60);
  return min < 60 ? tx("issue.span.min", { n: min }) : tx("chat.time.hourMin", { h: Math.floor(min / 60), m: min % 60 });
}

export function clockText(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return tx("chat.time.unknown");
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 우편함 한 줄의 전달 상태 — **글자로**. 서버가 실은 사실 셋으로 문장을 고른다. */
export function deliveryText(m: Pick<Message, "deliveredAt" | "deliveredTo" | "sessionId" | "sessionEndedAt">): {
  glyph: string;
  text: string;
  tone: "muted" | "success" | "error";
} {
  if (m.deliveredAt)
    return { glyph: "✓", text: `${tx("chat.delivery.read")}${m.deliveredTo ? ` · ${m.deliveredTo}` : ""}`, tone: "success" };
  if (m.sessionId && m.sessionEndedAt) return { glyph: "✕", text: tx("chat.delivery.lost"), tone: "error" };
  if (m.sessionId) return { glyph: "○", text: tx("chat.delivery.waitSession"), tone: "muted" };
  return { glyph: "○", text: tx("chat.delivery.waitNext"), tone: "muted" };
}
